import { useState } from 'react';
import { motion } from 'motion/react';
import { Button, Csel, Icon, Switch, useToast } from '@efira/ui';

import { coins as coinsWord } from '../../bindings/labels';

import {
  useGifts,
  useSaveGiftCard,
  useSaveStream,
  useSendGiftCard,
  useStream,
} from '../../api/queries';

/**
 * Эффекты подарков.
 *
 * УРЕЗАНИЕ, ОБЪЯВЛЕННОЕ В SPEC 1.1: у эталона пятнадцать карточек с
 * уникальными анимациями, у нас ПЯТЬ УНИВЕРСАЛЬНЫХ эффектов, назначаемых
 * подаркам из нашего каталога. Пятнадцать уникальных анимаций - работа
 * художника, а не разработчика, и делать её заглушками нечестно.
 *
 * Карточка подарка при этом полноценная: название, цена в монетах,
 * длительность, включение, приглушение звука, предпросмотр одиночный и
 * серией, отправка на оверлей.
 */

const EFFECT_NAMES: Record<string, string> = {
  confetti: 'Конфетти',
  shockwave: 'Волна',
  rain: 'Дождь',
  flash: 'Вспышка',
  hearts: 'Сердца',
};

export function GiftFx() {
  const stream = useStream();
  const gifts = useGifts();
  const save = useSaveStream();
  const card = useSaveGiftCard();
  const send = useSendGiftCard();
  const toast = useToast();

  const sendCard = (giftId: string, count: number) => {
    send.mutate(
      { giftId, count },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title: count > 1 ? `Серия из ${count} ушла на оверлей` : 'Эффект ушёл на оверлей',
          });
        },
      },
    );
  };

  const data = stream.data;
  const cards = Object.values(data?.giftCards ?? {});

  /* Подарки для добавления - из НАШЕГО каталога: эффект назначается тому,
     что зритель реально может отправить. */
  const catalog = gifts.data?.gifts ?? [];

  /*
   * В СПИСКЕ ДОБАВЛЕНИЯ - ПЕРВЫЕ ШЕСТЬДЕСЯТ, В ПОДПИСЯХ КАРТОЧЕК - ВЕСЬ
   * СПРАВОЧНИК.
   *
   * Прежняя редакция резала каталог до шестидесяти позиций ОДИН раз и
   * искала по обрезанному списку имя карточки: заведённая заранее карточка
   * подарка, не попавшего в первую шестидесятку, показывала идентификатор
   * «heart-me» и «0 монет» вместо названия и цены. Ровно та строка
   * «unknown», ради отсутствия которой заводской набор и сверяется со
   * справочником на сервере.
   */
  const offered = catalog.slice(0, 60);
  const free = offered.filter((gift) => data?.giftCards[gift.id] === undefined);

  return (
    <div data-giftfx="root" className="flex flex-col gap-4">
      <h2 className="text-foreground text-xl font-semibold">Эффекты подарков</h2>

      <p className="text-foreground/80 text-sm">
        Полноэкранная анимация на дешёвые подарки, которые TikTok не анимирует сам. Пять
        универсальных эффектов — любой можно назначить любому подарку. Изменения применяются к
        идущему эфиру сразу.
      </p>

      <label data-giftfx="master" className="flex items-start gap-3 text-sm">
        <Switch
          data-giftfx-master
          checked={data?.giftFx === true}
          onCheckedChange={(next) => {
            save.mutate({ giftFx: next === true });
          }}
          className="mt-1"
        />
        <span className="flex flex-col">
          <span className="text-foreground text-base font-semibold">
            Показывать эффекты на оверлее
          </span>
          <span className="text-foreground/90 text-sm">
            Выключает все эффекты сразу; настройки карточек сохраняются.
          </span>
        </span>
      </label>

      <div data-control-row="giftfx-add" className="@container flex items-center gap-2">
        <span className="text-foreground min-w-0 flex-1 truncate text-sm">
          Карточек: {cards.length}
        </span>

        <Csel
          value=""
          aria-label="Добавить подарок"
          placeholder="Добавить подарок"
          triggerClassName="w-56"
          options={free.map((gift) => ({ value: gift.id, label: `${gift.name} · ${gift.coins}` }))}
          onChange={(giftId) => {
            card.mutate({ giftId });
          }}
        />
      </div>

      {cards.length === 0 ? (
        <p data-giftfx="empty" className="text-foreground/80 text-sm">
          Пока ни одного подарка. Выберите подарок выше — и на него будет срабатывать эффект.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {cards.map((item) => {
            const gift = catalog.find((entry) => entry.id === item.giftId);

            return (
              <li
                key={item.giftId}
                data-giftfx-card={item.giftId}
                data-control-row="giftfx-card"
                /* При выключенном общем тумблере карточки ПРИТУШЕНЫ: иначе
                   зелёный тумблер на карточке обещает эффект, которого не
                   будет. */
                className={`border-foreground/10 rounded-lg @container flex flex-col gap-2 border p-2 ${
                  data?.giftFx === true ? '' : 'opacity-60'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    data-gift-name={gift?.name ?? item.giftId}
                    className="text-foreground min-w-0 flex-1 truncate text-sm font-semibold"
                  >
                    {gift?.name ?? item.giftId}
                  </span>

                  {/* Склонение общим форматтером: «1 монет» ломает строку
                      на каждом дешёвом подарке. */}
                  {/* ПОЛНЫЙ ЦВЕТ, а не `/90`: счётчик набран 12 px, и на белом
                      поверхности окна приглушение давало 4,24 при
                      требуемых 4,5 - axe ловит это на каждой карточке.
                      Приглушение здесь ничего не сообщало: рядом нет
                      более важного текста, от которого счётчик надо
                      отодвинуть. */}
                  <span className="text-foreground shrink-0 text-xs tabular-nums">
                    {coinsWord(gift?.coins ?? 0)}
                  </span>

                  <label className="flex shrink-0 items-center gap-1 text-xs">
                    <Switch
                      data-giftfx-toggle={item.giftId}
                      aria-label={`Эффект для «${gift?.name ?? item.giftId}»`}
                      checked={item.enabled}
                      onCheckedChange={(next) => {
                        card.mutate({ giftId: item.giftId, enabled: next === true });
                      }}
                    />
                    {/* Без согласования с родом названия: «Включён» ломается
                        на каждом втором подарке. */}
                    Эффект включён
                  </label>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-1 text-xs">
                    Эффект
                    <Csel
                      value={item.effect}
                      aria-label="Эффект"
                      triggerClassName="w-40"
                      options={(data?.effects ?? []).map((effect) => ({
                        value: effect,
                        label: EFFECT_NAMES[effect] ?? effect,
                      }))}
                      onChange={(effect) => {
                        card.mutate({ giftId: item.giftId, effect });
                      }}
                    />
                  </label>

                  <label className="flex flex-col gap-1 text-xs">
                    Длительность
                    <Csel
                      value={String(item.seconds)}
                      aria-label="Длительность эффекта"
                      triggerClassName="w-28"
                      options={Array.from({ length: 12 }, (_, index) => {
                        const value = String((index + 1) * 5);
                        return { value, label: `${value} с` };
                      })}
                      onChange={(seconds) => {
                        card.mutate({ giftId: item.giftId, seconds: Number(seconds) });
                      }}
                    />
                  </label>

                  <label className="flex items-center gap-1 text-xs">
                    <Switch
                      data-giftfx-sound={item.giftId}
                      checked={item.muted}
                      onCheckedChange={(next) => {
                        card.mutate({ giftId: item.giftId, muted: next === true });
                      }}
                    />
                    Без звука
                  </label>

                  {/* Предпросмотр ЛОКАЛЬНЫЙ, отправка - настоящая. Разница
                      подписана, а не подразумевается. */}
                  <Preview effect={item.effect} giftId={item.giftId} />

                  {/* Не главная: у раздела их по одной на подарок, а главная
                      кнопка поверхности одна (блок 2 единицы 21). */}
                  <Button
                    variant="secondary"
                    size="s"
                    data-giftfx-send={item.giftId}
                    onClick={() => {
                      sendCard(item.giftId, 1);
                    }}
                  >
                    Показать на оверлее
                  </Button>

                  <Button
                    variant="secondary"
                    size="s"

                    data-giftfx-playbulk={item.giftId}
                    onClick={() => {
                      sendCard(item.giftId, 5);
                    }}
                  >
                    Серией ×5 на оверлей
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Одиночный предпросмотр: анимация проигрывается ЗДЕСЬ, в окне.
 *
 * Не тост со словом «предпросмотр»: человек выбирает эффект глазами, и
 * показать надо эффект, а не его название. В эфир при этом ничего не
 * уходит - для этого есть соседняя кнопка, и разница подписана.
 *
 * Анимация - Motion, как требует дизайн-система; `prefers-reduced-motion`
 * исполняется - при нём частицы не летают, показывается только вспышка
 * подписи.
 */
function Preview({ effect, giftId }: { effect: string; giftId: string }) {
  const [playing, setPlaying] = useState(false);
  const calm = usePrefersReducedMotion();

  return (
    <>
      <Button
        variant="secondary"
        size="s"
        className="gap-1"
        data-giftfx-play={giftId}
        title="Предпросмотр здесь — в эфир не уйдёт"
        onClick={() => {
          setPlaying(true);
          window.setTimeout(() => {
            setPlaying(false);
          }, 1800);
        }}
      >
        <Icon name="play" size="sm" />
        Показать здесь
      </Button>

      {playing && (
        <div
          data-giftfx-preview={giftId}
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[140] grid place-items-center"
        >
          <motion.span
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="bg-background/90 text-foreground rounded-lg px-4 py-2 text-lg font-semibold shadow"
          >
            {EFFECT_NAMES[effect] ?? effect}
          </motion.span>

          {!calm &&
            Array.from({ length: 14 }, (_, index) => (
              <motion.i
                key={index}
                initial={{ x: 0, y: 0, opacity: 1 }}
                animate={{
                  x: Math.cos((index / 14) * Math.PI * 2) * 220,
                  y: Math.sin((index / 14) * Math.PI * 2) * 160,
                  opacity: 0,
                }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                className="absolute size-3 rounded-sm"
                style={{ backgroundColor: `hsl(${index * 26} 80% 60%)` }}
              />
            ))}
        </div>
      )}
    </>
  );
}

/** Уважение к «поменьше движения»: правило дизайн-системы, не украшение. */
function usePrefersReducedMotion(): boolean {
  const [calm] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
  );

  return calm;
}
